import type { RadioGroupOption } from "@/components/ui/Radio";
import {
  type BillingMethod,
  type LabNetwork,
  RequestGroup,
  type ServiceRequest,
} from "@curai/js-fhir-tools";
import { useMemo } from "react";
import { useLabOrderEntryActions } from "../network/useLabOrderEntryActions";

export const useOrderFormOptions = ({
  patientId,
  requestGroup,
  serviceRequests,
  selectedNetwork,
  selectedBillingMethod,
}: {
  patientId: string;
  requestGroup: RequestGroup;
  serviceRequests: ServiceRequest[];
  selectedNetwork?: LabNetwork;
  selectedBillingMethod?: BillingMethod;
}) => {
  const {
    data: labOrderEntryActions,
    isPending: isPendingLabOrderEntryActions,
  } = useLabOrderEntryActions({
    patientId: patientId,
  });

  // Bill to options visibility and enabled state are determined by the lab order
  // entry actions and current state of the form.
  const billToOptions = useMemo(() => {
    const availableMethods: RadioGroupOption[] = [];

    // Self-pay is always available
    availableMethods.push({
      label:
        RequestGroup.BILLING_METHOD_LABELS[
          RequestGroup.BILLING_METHODS.SELF_PAY
        ],
      value: RequestGroup.BILLING_METHODS.SELF_PAY,
      optionDisabled: false,
    });

    // Client pay (aka Curai-Pay) should only be shown if it's available. It should
    // always be first in the list. If the lab network is Labcorp, it should be
    // disabled.
    if (labOrderEntryActions?.clientPayAvailable) {
      const clientPayOption: RadioGroupOption = {
        label:
          RequestGroup.BILLING_METHOD_LABELS[
            RequestGroup.BILLING_METHODS.CLIENT_PAY
          ],
        value: RequestGroup.BILLING_METHODS.CLIENT_PAY,
        optionDisabled: false,
      };

      if (selectedNetwork === RequestGroup.LAB_NETWORKS.LABCORP) {
        clientPayOption.optionDisabled = true;
        clientPayOption.tooltipText =
          "Client pay is not available for Labcorp.";
        clientPayOption.tooltipClassName = "max-w-72 leading-none";
      }

      availableMethods.unshift(clientPayOption);
    }

    // Insurance pay should always be shown. It should always be last in the list.
    // If it's not available, it should be visible butdisabled.
    if (labOrderEntryActions?.insurancePayAvailable) {
      availableMethods.push({
        label:
          RequestGroup.BILLING_METHOD_LABELS[
            RequestGroup.BILLING_METHODS.INSURANCE
          ],
        value: RequestGroup.BILLING_METHODS.INSURANCE,
        optionDisabled: false,
      });
    } else {
      availableMethods.push({
        label:
          RequestGroup.BILLING_METHOD_LABELS[
            RequestGroup.BILLING_METHODS.INSURANCE
          ],
        value: RequestGroup.BILLING_METHODS.INSURANCE,
        optionDisabled: true,
        tooltipText:
          "Add the patient's insurance in medical records to bill to insurance.",
        tooltipClassName: "max-w-72 leading-none",
      });
    }

    // Disable switching from client pay to insurance pay / self pay and vice versa
    // after tests have been added. This is because the order is already in a
    // state where it cannot be changed. This could be moved to the backend in
    // the future, but would require a large refactor: available order entry actions
    // do not look at the order state, so it would need to be put in the get labs
    // API, and the front end would need to look at both APIs to determine if change
    // billing method is available.
    if (serviceRequests?.length) {
      if (selectedBillingMethod === RequestGroup.BILLING_METHODS.CLIENT_PAY) {
        const insuranceOption = availableMethods.find(
          (method) => method.value === RequestGroup.BILLING_METHODS.INSURANCE,
        );
        const selfPayOption = availableMethods.find(
          (method) => method.value === RequestGroup.BILLING_METHODS.SELF_PAY,
        );
        if (insuranceOption) {
          insuranceOption.optionDisabled = true;
          insuranceOption.tooltipText =
            "Cannot switch from client pay to insurance pay after tests have been added. Cancel and start a new order to switch to insurance pay.";
          insuranceOption.tooltipClassName = "max-w-72 leading-none";
        }
        if (selfPayOption) {
          selfPayOption.optionDisabled = true;
          selfPayOption.tooltipText =
            "Cannot switch from client pay to self pay after tests have been added. Cancel and start a new order to switch to self pay.";
          selfPayOption.tooltipClassName = "max-w-72 leading-none";
        }
      } else if (
        selectedBillingMethod === RequestGroup.BILLING_METHODS.INSURANCE ||
        selectedBillingMethod === RequestGroup.BILLING_METHODS.SELF_PAY
      ) {
        const clientPayOption = availableMethods.find(
          (method) => method.value === RequestGroup.BILLING_METHODS.CLIENT_PAY,
        );
        if (clientPayOption) {
          clientPayOption.optionDisabled = true;
          clientPayOption.tooltipText =
            "Cannot switch from insurance pay or self pay to client pay after tests have been added. Cancel the tests to switch to client pay.";
          clientPayOption.tooltipClassName = "max-w-72 leading-none";
        }
      }
    }

    return availableMethods;
  }, [
    labOrderEntryActions?.insurancePayAvailable,
    labOrderEntryActions?.clientPayAvailable,
    serviceRequests?.length,
    selectedNetwork,
    selectedBillingMethod,
  ]);

  // Determine the reason why the change network button is disabled. This can either
  // be because available actions determined a specific network must be used, or
  // because tests have already been added to the order.
  const changeNetworkDisabledReason = useMemo(() => {
    if (!labOrderEntryActions?.changeLabNetworkAvailable) {
      return `This patient's sponsor client only allows ${requestGroup.labNetworkLabel}.`;
    }
    if (serviceRequests?.length) {
      return "Cannot change lab network after tests have been added. Cancel and start a new order to change the lab network.";
    }
    return undefined;
  }, [
    labOrderEntryActions?.changeLabNetworkAvailable,
    requestGroup.labNetworkLabel,
    serviceRequests?.length,
  ]);

  // Network options visibility and enabled state are determined by the lab order
  // entry actions. If change network is disabled, the network options should be
  // disabled.
  const networkOptions = useMemo(() => {
    const options: RadioGroupOption[] = [];
    for (const network of Object.values(RequestGroup.LAB_NETWORKS)) {
      if (labOrderEntryActions?.enabledNetworks.includes(network)) {
        options.push({
          label: RequestGroup.LAB_NETWORK_LABELS[network],
          value: network,
          optionDisabled: Boolean(changeNetworkDisabledReason),
          tooltipText: changeNetworkDisabledReason,
          tooltipClassName: "max-w-72 leading-none",
        });
      }
    }
    return options;
  }, [labOrderEntryActions?.enabledNetworks, changeNetworkDisabledReason]);

  return {
    billToOptions,
    networkOptions,
    isPendingLabOrderEntryActions,
    changeNetworkDisabledReason,
  };
};
